import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// CONFIGS

// SERVICES

// CONSTANTS
import {
  EAppAccountAuthType,
  EAppAccountType,
} from 'v2/modules/system-setting/constants';

// TYPES
import { WithTemplateHelper } from 'v2/shared/patterns/TemplateHelper';

import { DialogConfig, IGetApiResponse } from '../../../shared';
import { IAppIntegrationAccount } from '../../../shared/interfaces/app-integration-account.interface';
import { AppIntegrationAccountApiService } from '../../../shared/services/api/app-integration/app-integration-account-api.service';

// COMPONENTS
import { UiConfirmationDialogComponent } from '../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { UiMessageDialogComponent } from '../../../ui/components/ui-message-dialog/ui-message-dialog.component';
import { AppIntegrationAccountFormComponent } from '../app-integration-account-form/app-integration-account-form.component';

import { PaginationConfig } from './../../../shared/configs/paginator.config';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';
import { IPagination } from './../../../shared/interfaces/paginator.interface';
import { TimelineListDialogComponent } from '../../../shared/components/timeline-list-dialog/timeline-list-dialog.component';
import { ChannelIntegrationStatusConstant } from '@app/core/_constant/channel.constants';

interface IAppIntegrationAccountsComponentData extends IAppIntegrationAccount {
  hasCredentials :boolean;
  showConnectStore :boolean;
}

@Component({
  selector: 'lum-app-integration-accounts',
  templateUrl: './app-integration-accounts.component.html',
  styleUrls: ['./app-integration-accounts.component.scss'],
})
export class AppIntegrationAccountsComponent
  implements OnInit, OnDestroy, WithTemplateHelper
{
  private readonly _appIntegrationAccounts = new BehaviorSubject<
    IAppIntegrationAccount[]
  >(<IAppIntegrationAccount[]>[]);
  private readonly _apiResponseData = new BehaviorSubject<IGetApiResponse>(
    <IGetApiResponse>{},
  );

  loading$ = new BehaviorSubject<boolean>(<boolean>false);
  appIntegrationAccounts$ = this._appIntegrationAccounts.asObservable();
  apiResponseData$ = this._apiResponseData.asObservable();
  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    path: '',
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };

  queryParams :{ [param :string] :string | string[] } = {
    limit: PaginationConfig.perPage.toString(),
    page: PaginationConfig.page.toString(),
    sortDirection: 'desc',
    sortField: 'created_at',
    includeChannelIntegrationMappingsCount: '1',
    relations: 'settings,channelIntegrationTransactions',
  };

  private onDestroy$ = new Subject();
  channelIntegrationStatusConstant = ChannelIntegrationStatusConstant;

  constructor(
    private appIntegrationAccountSvc :AppIntegrationAccountApiService,
    private snackBar :MatSnackBar,
    private dialog :MatDialog,
  ) {}

  ngOnInit() :void {
    this.getRecords();
  }

  ngOnDestroy() :void {}

  getRecords() {
    this.loading$.next(true);
    this.appIntegrationAccountSvc
      .get(this.queryParams)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IGetApiResponse) => {
          this.pagination = response.meta;
          this._apiResponseData.next(response);

          response.data.forEach((account :IAppIntegrationAccountsComponentData) => {
            if (
              account.credentials &&
              account.credentials.constructor.name === 'Object'
            ) {
              account.hasCredentials = true;
            } else {
              account.hasCredentials = false;
            }
            if (
              account.authType === EAppAccountAuthType.OAUTH &&
              (account.app === EAppAccountType.ECART ||
                account.app === EAppAccountType.ETSY ||
                account.app === EAppAccountType.FAIRE)
            ) {
              account.showConnectStore = true;
            }
          });

          this._appIntegrationAccounts.next(response.data);
          this.loading$.next(false);
        },
        error: (error) => {
          this.loading$.next(false);
          throwError(() => new Error(error));
        },
      });
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };

    this.getRecords();
  }

  onEditAccount(account :IAppIntegrationAccount) {
    this.onOpenFormModal(account);
  }

  onAuthorizeAccount(account :IAppIntegrationAccount) {
    this.appIntegrationAccountSvc.getOAuthAuthorizationUrl(account.id).subscribe({
      next: (response) => {
        console.log(
          `Redirecting to ${account.app} authorization URL:`,
          response.url,
        );
        window.location.href = response.url;
      },
      error: (error) => {
        this.snackBar.open(
          `Failed to get ${account.app} authorization URL`,
          'Fail',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );
      },
    });
  }

  onDeleteAccount(account :IAppIntegrationAccount) {
    if (account?.channelIntegrationMappingsCount) {
      const channels = account.channelIntegrationMappings.reduce(
        (holder :string[], channelIntegrationMapping) => {
          holder.push(channelIntegrationMapping.channel?.label);

          return holder;
        },
        [],
      );
      this.dialog.open(UiMessageDialogComponent, {
        data: {
          title: 'Unable to delete',
          message:
            'This account is associated with one or more channels. Prior to ' +
            'deletion, please disconnect this account from these channels ' +
            `[${channels.join(', ')}]`,
        },
        width: DialogConfig.sizes.small,
      });

      return;
    }
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message:
            'This will delete this account including shipstation stores and will also unlink store id from channels.',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.loading$.next(true);
          this.appIntegrationAccountSvc.delete(account.id).subscribe({
            next: () => {
              this.snackBar.open('Record deleted', 'Close', {
                duration: 3000,
              });
              this.loading$.next(false);
              this.getRecords();
            },
            error: (e) => {
              this.snackBar.open('Cannot delete this record.', 'Close', {
                duration: 3000,
              });
              this.loading$.next(false);
            },
          });
        }
      });
  }

  onOpenFormModal(account ?:IAppIntegrationAccount) {
    const config :MatDialogConfig = {
      width: DialogConfig.sizes.medium,
      disableClose: true,
      data: account ? { account } : {},
    };

    this.dialog
      .open(AppIntegrationAccountFormComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (integrationAccountData :IAppIntegrationAccount) => {
          if (integrationAccountData) {
            this.getRecords();
          }
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to save integration account',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  onFilterChange(searchQuery :string) :void {
    this.queryParams = {
      ...this.queryParams,
      searchQuery,
    };

    this.getRecords();
  }

  onViewTimelineList(channelIntegrationTransactions: any[] = []): void {
    if (!channelIntegrationTransactions?.length) {
      return;
    }

    this.dialog
      .open(TimelineListDialogComponent, {
        width: DialogConfig.Size.XX_LARGE,
        data: {
          title: 'Transaction Status List',
          items: channelIntegrationTransactions,
          fieldMap: {
            title: {
              path: 'status',
              transform: (status: string, item: any) => {
                try {
                  const meta = JSON.parse(item.metadata || '{}');
                  const operation = meta.operation || '';
                  return operation ? `${status} - ${operation}` : status;
                } catch {
                  return status;
                }
              },
            },
            subtitle: {
              path: 'response',
              transform: (response: string, item: any) => {
                try {
                  const parsed = JSON.parse(response || '{}');
                  return parsed.message || '';
                } catch {
                  return response;
                }
              },
            },
            date: {
              path: 'updatedAt',
              type: 'date',
            },
          },
        },
      });
  }

  get loading() :boolean {
    return this.loading$.getValue();
  }

  TemplateHelper = {
    canManageProducts: (account) =>
      // EDI accounts can always manage products.
      account.app === EAppAccountType.EDI ||
      // You can also manage products if "settings.get_products" is on.
      account.settings?.get_products === 'true',
    canManageCompanies: (account) =>
      account.settings?.sync_client_company === 'true',
  };
}
